import React, { useState, useEffect } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import {
    Plus, Edit2, Trash2, Calendar, Tag, User, CheckSquare, Square, MoreVertical,
    Clock, AlertCircle, Save, X, Filter, RefreshCw, Search, ChevronDown,
    ArrowRight, TrendingUp, Building, Home, ChevronRight, Bell, Settings,
    LogOut, Eye, Grid, Package2, Layers, Users, Target, Award, 
    Activity, Zap, Hash, FileText, Shield, BarChart3, PieChart,
    Bookmark, Star, ArrowUp, ArrowDown, Minimize2, Maximize2, Menu , Receipt , Briefcase
} from 'lucide-react';
import TaskCard from './TaskCard';
import CreateTaskModal from './CreateTaskModal';
import EditTaskModal from './EditTaskModal';

const UnitechKanbanBoard = ({ 
    projectId, 
    columns, 
    tasks, 
    onUpdateColumns, 
    onUpdateTasks,
    user,
    navigate,
    currentTime,
    notifications = 3
}) => {
    const [boardColumns, setBoardColumns] = useState(columns || []);
    const [boardTasks, setBoardTasks] = useState(tasks || []);
    const [showCreateTask, setShowCreateTask] = useState(false);
    const [selectedColumn, setSelectedColumn] = useState(null);
    const [showEditTask, setShowEditTask] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const [editingColumn, setEditingColumn] = useState(null);
    const [newColumnName, setNewColumnName] = useState('');
    const [isAddingColumn, setIsAddingColumn] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedPriority, setSelectedPriority] = useState('all');
    const [selectedAssignee, setSelectedAssignee] = useState('all');
    const [isLoading, setIsLoading] = useState(false);
    const [selectedTaskDetail, setSelectedTaskDetail] = useState(null);

    useEffect(() => {
        setBoardColumns(columns || []);
    }, [columns]);

    useEffect(() => {
        setBoardTasks(tasks || []);
    }, [tasks]);

    // Filter tasks based on search and filters
    const getFilteredTasks = () => {
        return boardTasks.filter(task => {
            const matchesSearch = !searchTerm || 
                (task.title && task.title.toLowerCase().includes(searchTerm.toLowerCase())) ||
                (task.description && task.description.toLowerCase().includes(searchTerm.toLowerCase()));
            
            const matchesPriority = selectedPriority === 'all' || task.priority === selectedPriority;
            const matchesAssignee = selectedAssignee === 'all' || task.assignee === selectedAssignee;
            
            return matchesSearch && matchesPriority && matchesAssignee;
        });
    };

    const handleDragEnd = (result) => {
        const { destination, source, draggableId, type } = result;

        if (!destination) return;

        if (
            destination.droppableId === source.droppableId &&
            destination.index === source.index
        ) {
            return;
        }

        if (type === 'column') {
            const newColumns = Array.from(boardColumns);
            const [reorderedColumn] = newColumns.splice(source.index, 1);
            newColumns.splice(destination.index, 0, reorderedColumn);

            setBoardColumns(newColumns);
            onUpdateColumns(newColumns);
            return;
        }

        const start = boardColumns.find(col => col._id === source.droppableId);
        const finish = boardColumns.find(col => col._id === destination.droppableId);

        if (start === finish) {
            const columnTasks = getFilteredTasks().filter(task => task.columnId === start._id);
            const [reorderedTask] = columnTasks.splice(source.index, 1);
            columnTasks.splice(destination.index, 0, reorderedTask);

            const newTasks = boardTasks.map(task => {
                if (task.columnId === start._id) {
                    const taskIndex = columnTasks.findIndex(t => t._id === task._id);
                    return { ...task, order: taskIndex };
                }
                return task;
            });

            setBoardTasks(newTasks);
            onUpdateTasks(newTasks);
        } else {
            const updatedTask = {
                ...boardTasks.find(task => task._id === draggableId),
                columnId: finish._id,
                order: destination.index
            };

            const newTasks = boardTasks.map(task => {
                if (task._id === draggableId) {
                    return updatedTask;
                }
                return task;
            });

            setBoardTasks(newTasks);
            onUpdateTasks(newTasks);
        }
    };

    const handleCreateTask = (columnId) => {
        setSelectedColumn(columnId);
        setShowCreateTask(true);
    };

    const handleTaskCreated = (newTask) => {
        const updatedTasks = [...boardTasks, newTask];
        setBoardTasks(updatedTasks);
        onUpdateTasks(updatedTasks);
        setShowCreateTask(false);
    };

    const handleEditTask = (task) => {
        setSelectedTask(task);
        setShowEditTask(true);
    };

    const handleTaskUpdated = (updatedTask) => {
        const updatedTasks = boardTasks.map(task =>
            task._id === updatedTask._id ? updatedTask : task
        );
        setBoardTasks(updatedTasks);
        onUpdateTasks(updatedTasks);
        setShowEditTask(false);
    };

    const handleDeleteTask = (taskId) => {
        if (window.confirm('Are you sure you want to delete this task?')) {
            const updatedTasks = boardTasks.filter(task => task._id !== taskId);
            setBoardTasks(updatedTasks);
            onUpdateTasks(updatedTasks);
        }
    };

    const handleAddColumn = () => {
        if (newColumnName.trim()) {
            const newColumn = {
                _id: Date.now().toString(),
                name: newColumnName.trim(),
                order: boardColumns.length,
                projectId: projectId
            };

            const updatedColumns = [...boardColumns, newColumn];
            setBoardColumns(updatedColumns);
            onUpdateColumns(updatedColumns);
            setNewColumnName('');
            setIsAddingColumn(false);
        }
    };

    const handleEditColumn = (columnId) => {
        const column = boardColumns.find(col => col._id === columnId);
        setEditingColumn(columnId);
        setNewColumnName(column.name);
    };

    const handleUpdateColumn = (columnId) => {
        if (newColumnName.trim()) {
            const updatedColumns = boardColumns.map(col =>
                col._id === columnId ? { ...col, name: newColumnName.trim() } : col
            );
            setBoardColumns(updatedColumns);
            onUpdateColumns(updatedColumns);
            setEditingColumn(null);
            setNewColumnName('');
        }
    };

    const handleDeleteColumn = (columnId) => {
        const columnTasks = boardTasks.filter(task => task.columnId === columnId);

        if (columnTasks.length > 0) {
            alert('Cannot delete column with tasks. Please move or delete all tasks first.');
            return;
        }

        if (window.confirm('Are you sure you want to delete this column?')) {
            const updatedColumns = boardColumns.filter(col => col._id !== columnId);
            setBoardColumns(updatedColumns);
            onUpdateColumns(updatedColumns);
        }
    };

    const getTasksForColumn = (columnId) => {
        return getFilteredTasks()
            .filter(task => task.columnId === columnId)
            .sort((a, b) => (a.order || 0) - (b.order || 0));
    };

    const getUserInitials = () => {
        if (!user) return 'U';
        const firstInitial = user.firstName ? user.firstName.charAt(0).toUpperCase() : '';
        const lastInitial = user.lastName ? user.lastName.charAt(0).toUpperCase() : '';
        return firstInitial + lastInitial || user.email.charAt(0).toUpperCase();
    };

    const getDisplayName = () => {
        if (!user) return 'Utilisateur';
        if (user.firstName && user.lastName) {
            return `${user.firstName} ${user.lastName}`;
        }
        return user.email;
    };

    const handleLogout = async () => {
        if (window.confirm('Are you sure you want to disconnect?')) {
            navigate('/login');
        }
    };

    const handleRefresh = () => {
        setIsLoading(true);
        setTimeout(() => {
            setIsLoading(false);
        }, 1000);
    };

    const getPriorityBadgeStyle = (priority) => {
        switch (priority) {
            case 'high':
                return 'status-out-of-stock';
            case 'medium':
                return 'status-low-stock';
            case 'low':
                return 'status-available';
            default:
                return 'status-available';
        }
    };

    const getColumnIcon = (columnName) => {
        if (!columnName || typeof columnName !== 'string') return Layers;
        const name = columnName.toLowerCase();
        if (name.includes('todo') || name.includes('backlog')) return Grid;
        if (name.includes('progress') || name.includes('doing')) return Activity;
        if (name.includes('review') || name.includes('testing')) return Eye;
        if (name.includes('done') || name.includes('complete')) return CheckSquare;
        return Layers;
    };

    const handleTaskClick = (task) => {
        setSelectedTaskDetail(task);
    };

    return (
        <>
        <div className="products-content">
                    {/* Page Header */}
                    <div className="page-header">
                        <h1 className="page-title">Project Kanban Board</h1>
                        <p className="page-subtitle">
                            Organize, track, and manage your project tasks with our intuitive drag-and-drop kanban workflow.
                        </p>
                        <div className="page-actions">
                            <button 
                                onClick={() => setIsAddingColumn(true)}
                                className="action-button"
                            >
                                <Plus style={{ width: '16px', height: '16px' }} />
                                Add Column
                            </button>
                            <button className="action-button secondary">
                                <Users style={{ width: '16px', height: '16px' }} />
                                Team View
                            </button>
                            <button className="action-button secondary">
                                <Calendar style={{ width: '16px', height: '16px' }} />
                                Timeline
                            </button>
                            <button className="action-button secondary">
                                <PieChart style={{ width: '16px', height: '16px' }} />
                                Analytics
                            </button>
                        </div>
                    </div>

                    {/* Filters Section */}
                    <div className="filters-section">
                        <div className="filters-row">
                            <div className="filter-group">
                                <label className="filter-label">Priority Filter</label>
                                <select
                                    value={selectedPriority}
                                    onChange={(e) => setSelectedPriority(e.target.value)}
                                    className="filter-select"
                                >
                                    <option value="all">All Priorities</option>
                                    <option value="high">🔴 High Priority</option>
                                    <option value="medium">🟡 Medium Priority</option>
                                    <option value="low">🟢 Low Priority</option>
                                </select>
                            </div>

                            <div className="filter-group">
                                <label className="filter-label">Team Member</label>
                                <select
                                    value={selectedAssignee}
                                    onChange={(e) => setSelectedAssignee(e.target.value)}
                                    className="filter-select"
                                >
                                    <option value="all">All Team Members</option>
                                </select>
                            </div>

                            <div className="filter-group">
                                <label className="filter-label" style={{ visibility: 'hidden' }}>Actions</label>
                                <button className="action-button secondary">
                                    <Filter style={{ width: '16px', height: '16px' }} />
                                    Advanced Filters
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Kanban Board */}
                    <div className="products-table-section">
                        <div className="table-header">
                            <h2 className="table-title">Project Tasks Board</h2>
                            <div className="table-stats">
                                <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    <Target style={{ width: '16px', height: '16px' }} />
                                    {getFilteredTasks().length} task{getFilteredTasks().length !== 1 ? 's' : ''} visible
                                    {isLoading && ' (refreshing...)'}
                                </span>
                            </div>
                        </div>

                        <div style={{ padding: '32px', minHeight: '600px', overflow: 'auto' }}>
                            <DragDropContext onDragEnd={handleDragEnd}>
                                <Droppable droppableId="board" type="column" direction="horizontal">
                                    {(provided) => (
                                        <div
                                            {...provided.droppableProps}
                                            ref={provided.innerRef}
                                            style={{
                                                display: 'flex',
                                                gap: '24px',
                                                minHeight: '500px',
                                                paddingBottom: '20px'
                                            }}
                                        >
                                            {boardColumns.map((column, index) => {
                                                const IconComponent = getColumnIcon(column.name);
                                                return (
                                                    <Draggable
                                                        key={column._id}
                                                        draggableId={column._id}
                                                        index={index}
                                                    >
                                                        {(provided, snapshot) => (
                                                            <div
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                style={{
                                                                    ...provided.draggableProps.style,
                                                                    minWidth: '320px',
                                                                    width: '320px',
                                                                    background: 'white',
                                                                    borderRadius: '12px',
                                                                    border: '1px solid rgba(30, 64, 175, 0.1)',
                                                                    display: 'flex',
                                                                    flexDirection: 'column',
                                                                    maxHeight: '80vh',
                                                                    boxShadow: snapshot.isDragging ? '0 20px 40px rgba(0, 0, 0, 0.15)' : '0 4px 12px rgba(0, 0, 0, 0.05)',
                                                                    transform: snapshot.isDragging ? 'rotate(2deg) scale(1.02)' : 'none',
                                                                    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
                                                                }}
                                                            >
                                                                {/* Column Header */}
                                                                <div
                                                                    {...provided.dragHandleProps}
                                                                    style={{
                                                                        padding: '20px',
                                                                        borderBottom: '1px solid rgba(30, 64, 175, 0.1)',
                                                                        display: 'flex',
                                                                        justifyContent: 'space-between',
                                                                        alignItems: 'center',
                                                                        background: 'linear-gradient(135deg, #1e40af, #0ea5e9)',
                                                                        borderRadius: '12px 12px 0 0',
                                                                        cursor: 'grab',
                                                                        color: 'white'
                                                                    }}
                                                                >
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                                                                        {editingColumn === column._id ? (
                                                                            <div style={{ display: 'flex', gap: '8px', width: '100%' }}>
                                                                                <input
                                                                                    type="text"
                                                                                    value={newColumnName}
                                                                                    onChange={(e) => setNewColumnName(e.target.value)}
                                                                                    style={{
                                                                                        flex: 1,
                                                                                        padding: '8px 12px',
                                                                                        border: '1px solid rgba(255, 255, 255, 0.3)',
                                                                                        borderRadius: '8px',
                                                                                        fontSize: '14px',
                                                                                        background: 'rgba(255, 255, 255, 0.2)',
                                                                                        color: 'white'
                                                                                    }}
                                                                                    onKeyPress={(e) => {
                                                                                        if (e.key === 'Enter') {
                                                                                            handleUpdateColumn(column._id);
                                                                                        }
                                                                                        if (e.key === 'Escape') {
                                                                                            setEditingColumn(null);
                                                                                            setNewColumnName('');
                                                                                        }
                                                                                    }}
                                                                                    autoFocus
                                                                                />
                                                                                <button
                                                                                    onClick={() => handleUpdateColumn(column._id)}
                                                                                    style={{
                                                                                        padding: '8px',
                                                                                        border: 'none',
                                                                                        background: 'rgba(16, 185, 129, 0.8)',
                                                                                        color: 'white',
                                                                                        borderRadius: '6px',
                                                                                        cursor: 'pointer'
                                                                                    }}
                                                                                >
                                                                                    <Save style={{ width: '14px', height: '14px' }} />
                                                                                </button>
                                                                                <button
                                                                                    onClick={() => {
                                                                                        setEditingColumn(null);
                                                                                        setNewColumnName('');
                                                                                    }}
                                                                                    style={{
                                                                                        padding: '8px',
                                                                                        border: 'none',
                                                                                        background: 'rgba(239, 68, 68, 0.8)',
                                                                                        color: 'white',
                                                                                        borderRadius: '6px',
                                                                                        cursor: 'pointer'
                                                                                    }}
                                                                                >
                                                                                    <X style={{ width: '14px', height: '14px' }} />
                                                                                </button>
                                                                            </div>
                                                                        ) : (
                                                                            <>
                                                                                <div style={{
                                                                                    width: '32px',
                                                                                    height: '32px',
                                                                                    background: 'rgba(255, 255, 255, 0.2)',
                                                                                    borderRadius: '10px',
                                                                                    display: 'flex',
                                                                                    alignItems: 'center',
                                                                                    justifyContent: 'center'
                                                                                }}>
                                                                                    <IconComponent style={{ width: '16px', height: '16px' }} />
                                                                                </div>
                                                                                <h3 style={{ 
                                                                                    margin: 0, 
                                                                                    fontSize: '16px', 
                                                                                    fontWeight: '700'
                                                                                }}>
                                                                                    {column.name}
                                                                                </h3>
                                                                                <span style={{
                                                                                    background: 'rgba(255, 255, 255, 0.25)',
                                                                                    color: 'white',
                                                                                    padding: '4px 10px',
                                                                                    borderRadius: '16px',
                                                                                    fontSize: '12px',
                                                                                    fontWeight: '600'
                                                                                }}>
                                                                                    {getTasksForColumn(column._id).length}
                                                                                </span>
                                                                            </>
                                                                        )}
                                                                    </div>

                                                                    <div style={{ display: 'flex', gap: '6px' }}>
                                                                        <button
                                                                            onClick={() => handleCreateTask(column._id)}
                                                                            className="header-button"
                                                                            style={{ color: 'white' }}
                                                                            title="Add Task"
                                                                        >
                                                                            <Plus style={{ width: '16px', height: '16px' }} />
                                                                        </button>

                                                                        <div style={{ position: 'relative', display: 'inline-block' }}>
                                                                            <button 
                                                                                className="header-button" 
                                                                                style={{ color: 'white' }}
                                                                            >
                                                                                <MoreVertical style={{ width: '16px', height: '16px' }} />
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Column Content */}
                                                                <Droppable droppableId={column._id} type="task">
                                                                    {(provided, snapshot) => (
                                                                        <div
                                                                            ref={provided.innerRef}
                                                                            {...provided.droppableProps}
                                                                            style={{
                                                                                flex: 1,
                                                                                padding: '20px',
                                                                                display: 'flex',
                                                                                flexDirection: 'column',
                                                                                gap: '16px',
                                                                                background: snapshot.isDraggingOver ? 'rgba(30, 64, 175, 0.02)' : 'transparent',
                                                                                borderRadius: '0 0 12px 12px',
                                                                                overflow: 'auto',
                                                                                transition: 'all 0.2s ease',
                                                                                border: snapshot.isDraggingOver ? '2px dashed rgba(30, 64, 175, 0.3)' : '2px dashed transparent'
                                                                            }}
                                                                        >
                                                                            {getTasksForColumn(column._id).map((task, index) => (
                                                                                <Draggable
                                                                                    key={task._id}
                                                                                    draggableId={task._id}
                                                                                    index={index}
                                                                                >
                                                                                    {(provided, snapshot) => (
                                                                                        <div
                                                                                            ref={provided.innerRef}
                                                                                            {...provided.draggableProps}
                                                                                            {...provided.dragHandleProps}
                                                                                            onClick={() => handleTaskClick(task)}
                                                                                            style={{
                                                                                                ...provided.draggableProps.style,
                                                                                                background: 'white',
                                                                                                borderRadius: '12px',
                                                                                                padding: '20px',
                                                                                                border: '1px solid rgba(30, 64, 175, 0.1)',
                                                                                                boxShadow: snapshot.isDragging ? '0 20px 40px rgba(0, 0, 0, 0.15)' : '0 4px 12px rgba(0, 0, 0, 0.05)',
                                                                                                cursor: 'pointer',
                                                                                                transform: snapshot.isDragging ? 'rotate(3deg) scale(1.02)' : 'none',
                                                                                                transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                                                                                                position: 'relative',
                                                                                                overflow: 'hidden'
                                                                                            }}
                                                                                        >
                                                                                            {/* Priority indicator bar */}
                                                                                            <div style={{
                                                                                                position: 'absolute',
                                                                                                top: 0,
                                                                                                left: 0,
                                                                                                width: '4px',
                                                                                                height: '100%',
                                                                                                background: task.priority === 'high' ? '#ef4444' : 
                                                                                                          task.priority === 'medium' ? '#f59e0b' : '#10b981',
                                                                                                borderRadius: '0 4px 4px 0'
                                                                                            }} />

                                                                                            {/* Task Header */}
                                                                                            <div style={{
                                                                                                display: 'flex',
                                                                                                justifyContent: 'space-between',
                                                                                                alignItems: 'flex-start',
                                                                                                marginBottom: '16px'
                                                                                            }}>
                                                                                                <h4 className="product-name" style={{
                                                                                                    margin: 0,
                                                                                                    fontSize: '15px',
                                                                                                    flex: 1,
                                                                                                    paddingRight: '12px'
                                                                                                }}>
                                                                                                    {task.title}
                                                                                                </h4>
                                                                                                <div style={{ display: 'flex', gap: '4px', opacity: 0, transition: 'opacity 0.2s ease' }} className="task-actions">
                                                                                                    <button
                                                                                                        onClick={(e) => {
                                                                                                            e.stopPropagation();
                                                                                                            handleEditTask(task);
                                                                                                        }}
                                                                                                        className="action-icon edit"
                                                                                                        title="Edit Task"
                                                                                                    >
                                                                                                        <Edit2 style={{ width: '14px', height: '14px' }} />
                                                                                                    </button>
                                                                                                    <button
                                                                                                        onClick={(e) => {
                                                                                                            e.stopPropagation();
                                                                                                            handleDeleteTask(task._id);
                                                                                                        }}
                                                                                                        className="action-icon delete"
                                                                                                        title="Delete Task"
                                                                                                    >
                                                                                                        <Trash2 style={{ width: '14px', height: '14px' }} />
                                                                                                    </button>
                                                                                                </div>
                                                                                            </div>

                                                                                            {/* Task Description */}
                                                                                            {task.description && (
                                                                                                <p style={{
                                                                                                    margin: '0 0 16px 0',
                                                                                                    fontSize: '13px',
                                                                                                    color: '#64748b',
                                                                                                    lineHeight: '1.5',
                                                                                                    display: '-webkit-box',
                                                                                                    WebkitLineClamp: 2,
                                                                                                    WebkitBoxOrient: 'vertical',
                                                                                                    overflow: 'hidden'
                                                                                                }}>
                                                                                                    {task.description}
                                                                                                </p>
                                                                                            )}

                                                                                            {/* Task Tags */}
                                                                                            {task.tags && task.tags.length > 0 && (
                                                                                                <div style={{ 
                                                                                                    display: 'flex', 
                                                                                                    flexWrap: 'wrap', 
                                                                                                    gap: '6px', 
                                                                                                    marginBottom: '16px' 
                                                                                                }}>
                                                                                                    {task.tags.slice(0, 3).map((tag, idx) => (
                                                                                                        <span
                                                                                                            key={idx}
                                                                                                            className="type-badge type-inventory"
                                                                                                            style={{
                                                                                                                fontSize: '10px',
                                                                                                                padding: '3px 8px',
                                                                                                                borderRadius: '12px'
                                                                                                            }}
                                                                                                        >
                                                                                                            #{tag}
                                                                                                        </span>
                                                                                                    ))}
                                                                                                    {task.tags.length > 3 && (
                                                                                                        <span style={{
                                                                                                            fontSize: '10px',
                                                                                                            color: '#64748b',
                                                                                                            fontWeight: '500'
                                                                                                        }}>
                                                                                                            +{task.tags.length - 3} more
                                                                                                        </span>
                                                                                                    )}
                                                                                                </div>
                                                                                            )}

                                                                                            {/* Task Footer */}
                                                                                            <div style={{
                                                                                                display: 'flex',
                                                                                                justifyContent: 'space-between',
                                                                                                alignItems: 'center',
                                                                                                fontSize: '12px'
                                                                                            }}>
                                                                                                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                                                                                    {/* Priority Badge */}
                                                                                                    <span className={`status-badge ${getPriorityBadgeStyle(task.priority)}`}>
                                                                                                        {task.priority === 'high' ? '🔴' : task.priority === 'medium' ? '🟡' : '🟢'} {task.priority?.toUpperCase() || 'MEDIUM'}
                                                                                                    </span>

                                                                                                    {/* Due Date */}
                                                                                                    {task.dueDate && (
                                                                                                        <div style={{ 
                                                                                                            display: 'flex', 
                                                                                                            alignItems: 'center', 
                                                                                                            gap: '4px',
                                                                                                            color: new Date(task.dueDate) < new Date() ? '#ef4444' : '#64748b'
                                                                                                        }}>
                                                                                                            <Calendar style={{ width: '12px', height: '12px' }} />
                                                                                                            <span>{new Date(task.dueDate).toLocaleDateString('fr-FR')}</span>
                                                                                                        </div>
                                                                                                    )}
                                                                                                </div>

                                                                                                {/* Assignee Avatar */}
                                                                                                {task.assignee && (
                                                                                                    <div className="user-avatar" style={{
                                                                                                        width: '28px',
                                                                                                        height: '28px',
                                                                                                        fontSize: '11px'
                                                                                                    }} title={`Assigned to ${task.assignee}`}>
                                                                                                        {task.assignee.charAt(0).toUpperCase()}
                                                                                                    </div>
                                                                                                )}
                                                                                            </div>
                                                                                        </div>
                                                                                    )}
                                                                                </Draggable>
                                                                            ))}
                                                                            {provided.placeholder}

                                                                            {/* Empty Column State */}
                                                                            {getTasksForColumn(column._id).length === 0 && (
                                                                                <div style={{
                                                                                    textAlign: 'center',
                                                                                    padding: '48px 20px',
                                                                                    color: '#64748b',
                                                                                    background: 'rgba(248, 250, 252, 0.5)',
                                                                                    borderRadius: '12px',
                                                                                    border: '2px dashed #cbd5e1'
                                                                                }}>
                                                                                    <Package2 style={{ 
                                                                                        width: '40px', 
                                                                                        height: '40px', 
                                                                                        margin: '0 auto 16px',
                                                                                        opacity: 0.4,
                                                                                        color: '#1e40af'
                                                                                    }} />
                                                                                    <p style={{ margin: '0 0 16px', fontSize: '14px', fontWeight: '500' }}>
                                                                                        No tasks yet
                                                                                    </p>
                                                                                    <p style={{ margin: '0 0 16px', fontSize: '12px', color: '#94a3b8' }}>
                                                                                        Tasks will appear here when added
                                                                                    </p>
                                                                                    <button
                                                                                        onClick={() => handleCreateTask(column._id)}
                                                                                        className="action-button"
                                                                                        style={{
                                                                                            fontSize: '12px',
                                                                                            padding: '10px 20px'
                                                                                        }}
                                                                                    >
                                                                                        <Plus style={{ width: '14px', height: '14px' }} />
                                                                                        Add first task
                                                                                    </button>
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </Droppable>
                                                            </div>
                                                        )}
                                                    </Draggable>
                                                );
                                            })}
                                            {provided.placeholder}

                                            {/* Add Column */}
                                            <div style={{ minWidth: '320px', width: '320px' }}>
                                                {isAddingColumn ? (
                                                    <div style={{
                                                        background: 'white',
                                                        borderRadius: '12px',
                                                        border: '1px solid rgba(30, 64, 175, 0.1)',
                                                        padding: '20px',
                                                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
                                                    }}>
                                                        <input
                                                            type="text"
                                                            value={newColumnName}
                                                            onChange={(e) => setNewColumnName(e.target.value)}
                                                            placeholder="Enter column name..."
                                                            className="filter-input"
                                                            style={{
                                                                width: '100%',
                                                                marginBottom: '16px',
                                                                boxSizing: 'border-box'
                                                            }}
                                                            onKeyPress={(e) => {
                                                                if (e.key === 'Enter') {
                                                                    handleAddColumn();
                                                                }
                                                                if (e.key === 'Escape') {
                                                                    setIsAddingColumn(false);
                                                                    setNewColumnName('');
                                                                }
                                                            }}
                                                            autoFocus
                                                        />
                                                        <div style={{ display: 'flex', gap: '8px' }}>
                                                            <button
                                                                onClick={handleAddColumn}
                                                                disabled={!newColumnName.trim()}
                                                                className="action-button"
                                                                style={{
                                                                    flex: 1,
                                                                    opacity: newColumnName.trim() ? 1 : 0.5,
                                                                    cursor: newColumnName.trim() ? 'pointer' : 'not-allowed'
                                                                }}
                                                            >
                                                                <Save style={{ width: '14px', height: '14px' }} />
                                                                Create Column
                                                            </button>
                                                            <button
                                                                onClick={() => {
                                                                    setIsAddingColumn(false);
                                                                    setNewColumnName('');
                                                                }}
                                                                className="action-button secondary"
                                                                style={{ padding: '12px 16px' }}
                                                            >
                                                                <X style={{ width: '14px', height: '14px' }} />
                                                            </button>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <button
                                                        onClick={() => setIsAddingColumn(true)}
                                                        style={{
                                                            width: '100%',
                                                            minHeight: '200px',
                                                            border: '2px dashed #cbd5e1',
                                                            background: 'rgba(248, 250, 252, 0.3)',
                                                            borderRadius: '12px',
                                                            cursor: 'pointer',
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            gap: '16px',
                                                            color: '#64748b',
                                                            fontSize: '14px',
                                                            fontWeight: '500',
                                                            transition: 'all 0.3s ease'
                                                        }}
                                                        onMouseEnter={(e) => {
                                                            e.target.style.borderColor = '#1e40af';
                                                            e.target.style.background = 'rgba(30, 64, 175, 0.05)';
                                                            e.target.style.color = '#1e40af';
                                                            e.target.style.transform = 'translateY(-2px)';
                                                        }}
                                                        onMouseLeave={(e) => {
                                                            e.target.style.borderColor = '#cbd5e1';
                                                            e.target.style.background = 'rgba(248, 250, 252, 0.3)';
                                                            e.target.style.color = '#64748b';
                                                            e.target.style.transform = 'translateY(0)';
                                                        }}
                                                    >
                                                        <Plus style={{ width: '32px', height: '32px' }} />
                                                        <div>
                                                            <div style={{ fontWeight: '600', marginBottom: '4px' }}>Add New Column</div>
                                                            <div style={{ fontSize: '12px', opacity: 0.8 }}>Create a new workflow stage</div>
                                                        </div>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </Droppable>
                            </DragDropContext>
                        </div>
                    </div>

                    {/* Task Statistics */}
                    <div style={{
                        marginTop: '32px',
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
                        gap: '20px'
                    }}>
                        {boardColumns.map(column => {
                            const columnTasks = getTasksForColumn(column._id);
                            const IconComponent = getColumnIcon(column.name);
                            
                            return (
                                <div key={column._id} className="stat-card">
                                    <div className="stat-header">
                                        <div className="stat-icon blue">
                                            <IconComponent style={{ width: '20px', height: '20px', color: 'white' }} />
                                        </div>
                                    </div>
                                    
                                    <div className="stat-value">
                                        {columnTasks.length}
                                    </div>
                                    
                                    <div className="stat-label">
                                        {column.name} - {columnTasks.length === 1 ? 'Task' : 'Tasks'}
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Status Footer */}
                    <div className="status-footer">
                        <div className="status-indicator">
                            <div className="status-operational">
                                <div className="status-dot"></div>
                                System Operational
                            </div>
                        </div>
                        <div className="status-info">
                            <span>Last updated: {new Date().toLocaleString('fr-FR')}</span>
                        </div>
                    </div>
                </div>

            {/* Modals */}
            <CreateTaskModal
                show={showCreateTask}
                columnId={selectedColumn}
                onClose={() => setShowCreateTask(false)}
                onSubmit={handleTaskCreated}
            />

            <EditTaskModal
                show={showEditTask}
                task={selectedTask}
                columns={boardColumns}
                onClose={() => setShowEditTask(false)}
                onSubmit={handleTaskUpdated}
                onDelete={() => handleDeleteTask(selectedTask?._id)}
            />

            {/* Task Actions Hover Effect */}
            
        {/* Modals */}
        <CreateTaskModal
            show={showCreateTask}
            columnId={selectedColumn}
            onClose={() => setShowCreateTask(false)}
            onSubmit={handleTaskCreated}
        />

        <EditTaskModal
            show={showEditTask}
            task={selectedTask}
            columns={boardColumns}
            onClose={() => setShowEditTask(false)}
            onSubmit={handleTaskUpdated}
            onDelete={() => handleDeleteTask(selectedTask?._id)}
        />

       
        </>
    );
};

export default UnitechKanbanBoard;